import { WorkOrderStatusType } from './enums';
import type { Project } from './projectsType';
export const itemType = {
  String: 'string',
  Integer: 'integer',
  Float: 'float',
  Date: 'date',
  Boolean: 'boolean',
} as const;
export const itemTypeEnums = Object.values(itemType);

export type BomStatus =
  | 'PROCESSING'
  | 'ACTIVE'
  | 'SUPERSEDED'
  | 'ARCHIVED'
  | 'FAILED';

export type Release = {
  id: number;
  created_at: string;
  updated_at: string;
  number: string;
  project: Project['id'];
  // Release-level due date (ISO date string, e.g. "2026-10-01"). When set,
  // this date applies to all FabricationWorkOrders under the release. Null
  // means no release-level date is configured (existing per-work-order
  // values stay as-is for backwards compatibility).
  due_date: string | null;
  bom_file?: string;
  bom_file_name?: string;
  bom_status?: BomStatus;
  bom_source?: 'UPLOAD' | 'REPLACEMENT';
  bom_row_count?: number;
  bom_error_message?: string;
  uploaded_by?: number | null;
  uploaded_by_name?: string | null;
  supersedes?: number | null;
  replacement_reason?: string;
  activated_at?: string | null;
  archived_at?: string | null;
  dependency_summary?: Record<string, number>;
};

export type BomAuditEvent = {
  id: number;
  project: number;
  release: number;
  old_release: number | null;
  new_release: number | null;
  action: 'ACTIVATE' | 'ARCHIVE' | 'REPLACE' | 'FAIL';
  reason: string;
  actor: number | null;
  actor_name: string | null;
  created_at: string;
};

export type ReleasePartial = Partial<Release>;

// Discriminator distinguishing the kind of file a saved mapping parses. Part
// BOM mappings drive the parts list / production summary; Frame BOM mappings
// drive the frame quantity import.
export const bomMappingType = {
  Part: 'PART_BOM',
  Frame: 'FRAME_BOM',
} as const;
export type BomMappingType =
  (typeof bomMappingType)[keyof typeof bomMappingType];

export type BomMappingFieldConfig = {
  source: string;
  data_type?: (typeof itemType)[keyof typeof itemType];
  part_attribute?: boolean;
  summary_view?: boolean;
  required?: boolean;
  unit?: string;
};

export type BOMMappings = {
  name: string;
  mapping_type?: BomMappingType;
  mapping: {
    [key: string]: BomMappingFieldConfig;
  };
  created_by: number;
};

// Column mapping for the Frame BOM (frame_qty_file). Mirrors the Part BOM
// mapping pattern but is sent at release creation / re-import time and stored
// on the release. frame_number + quantity are required; attributes maps
// logical keys (description/type/system/dimensions/notes) to source columns.
export type FrameFieldMapping = {
  sheet_name: string;
  header_row_index: number;
  frame_number: string;
  quantity: string;
  attributes?: Record<string, string>;
};

export type SummaryView = {
  id: number;
  name: string;
  quantity: number;
  attributes?: Record<string, unknown>;
  project_id: number;
  release_id: number;
  work_order_identifier: string;
  work_order_status: WorkOrderStatusType;
  work_orders: {
    id: number;
    work_order_identifier: string;
    status: string;
    description: string;
    total_quantity: number;
    assembled_quantity: number;
    glazed_quantity: number;
    packed_quantity: number;
    shipped_quantity: number;
    installed_quantity: number;
    assigned_users?: {
      id: number;
      display_name: string;
    }[];
    updated_at?: string;
  }[];
  assigned_users?: {
    id: number;
    display_name: string;
  }[];
  stage_progress?: Record<
    'ASSEMBLY' | 'GLAZING' | 'PACKING' | 'SHIPPING' | 'INSTALLATION',
    {
      total_quantity: number;
      previous_completed_quantity: number | null;
      completed_quantity: number;
      remaining_quantity: number;
      ready: boolean;
    }
  >;
  current_stage?: string;
  blocked_reasons?: Partial<
    Record<'ASSEMBLY' | 'GLAZING' | 'INSTALLATION', string | null>
  >;
  qc_summary?: Record<
    'ASSEMBLY' | 'GLAZING' | 'INSTALLATION',
    {
      pass: number;
      fail: number;
      info: number;
      open: number;
      closed: number;
      total: number;
    }
  >;
  checklist_summary?: Record<
    'ASSEMBLY' | 'GLAZING' | 'INSTALLATION',
    {
      status: string;
      last_submitted_at: string | null;
    }
  >;
  override_summary?: {
    has_override_completion: boolean;
    override_completion_count: number;
    latest_override: {
      step: string;
      completed_by: string | null;
      assigned_user_names: string[];
      override_reason: string | null;
      created_at: string | null;
    } | null;
  };
  last_updated?: string | null;
  frame_drawing_available?: boolean;
  kit_readiness_pct: number;
  kit_shortage_count: number;
  kit_shortages: {
    part_id: number;
    part_number: string;
    required_qty: number;
    available_qty: number;
    short_qty: number;
  }[];
};
